import { Image, View } from "@aws-amplify/ui-react"
import { useContext, useState } from "react"
import RouterContext from "../context/RouterContext.js"
import useBranding from "../hook/UseBranding.js"
import { FaceLivenessDetectionQueryParametersSchema } from "../schemata/biometry/FaceLivenessDetectionQueryParameters.js"
import style from "../style/Page.module.css"
import InvalidSessionFaceLivenessDetectionError from "../support/error/InvalidSessionFaceLivenessDetectionError.js"
import Validate from "../support/language/Validate.js"
import BrandedThemeProvider from "./BrandedThemeProvider.js"
import FaceLivenessDetector from "./FaceLivenessDetector.js"
import FaceLivenessDetectorErrorView from "./FaceLivenessDetectorErrorView.js"
import Landing from "./Landing.js"

type Properties = Readonly<{}>

/**
 * @description
 *	The entry point of the Face Liveness Detection process: the landing, then
 *	the camera stage.
 *
 * @remarks
 *	The attribution of Sanii is rendered by the landing on its own zone, so
 *	the page footer only exists during the camera stage, where the widget owns
 *	the whole screen.
 */
export default function Application({}: Properties) {
	const uri = useContext(RouterContext)
	const queryParameters = Validate(FaceLivenessDetectionQueryParametersSchema, {
		biometryId: uri?.queryParameters.get("biometryId"),
		context: uri?.queryParameters.get("context") || undefined,
		dsa: uri?.queryParameters.get("dsa"),
		expirationTimestamp: uri?.queryParameters.get("expirationTimestamp"),
		integrationId: uri?.queryParameters.get("integrationId"),
		locale: uri?.queryParameters.get("locale"),
		redirectUri: uri?.queryParameters.get("redirectUri"),
		sessionId: uri?.queryParameters.get("sessionId"),
		signature: uri?.queryParameters.get("signature"),
		stageVersion: uri?.queryParameters.get("stageVersion")
	})
	const { branding, settled } = useBranding(queryParameters?.integrationId)
	const [isStarted, setIsStarted] = useState(false)
	return (
		<BrandedThemeProvider branding={branding} isBrandingSettled={settled}>
			<View as={"main"} className={style["page-view"]}>
				{queryParameters === undefined ? (
					<FaceLivenessDetectorErrorView
						faceLivenessDetectionError={InvalidSessionFaceLivenessDetectionError}
						source="manual"
					/>
				) : isStarted ? (
					<FaceLivenessDetector sessionId={queryParameters.sessionId} />
				) : (
					<Landing branding={branding} isBrandingSettled={settled} onStart={() => setIsStarted(true)} />
				)}
				{isStarted && (
					<View className={style["page-footer"]}>
						<Image alt="Sanii" className={style["brand"]} src="/brand/sanii.png" />
					</View>
				)}
			</View>
		</BrandedThemeProvider>
	)
}
